<script lang="ts" setup>
import { Bar } from 'vue-chartjs';
import { colors } from 'quasar';
import { ChartOptions } from 'chart.js';
import { t } from '~/plugins/i18n';
import { upperFirst } from 'lodash-es';
import { formatNumber } from '~/utils/localeUtils';
import { DashboardDetailsCardProps } from './_types';
import { DashboardType } from '~/enums/_types';

const props = defineProps<DashboardDetailsCardProps>();
const activeTab = ref<'summary' | 'chart'>('summary');

watch(
    () => props.data.Type,
    () => {
        activeTab.value = 'summary';
    }
);
const chartLabelString = (value: number, unit: string | null, label: string | null = null): string => {
    return (
        (label == null ? '' : label + ': ') + formatNumber(value) + (unit ? ' ' + t(`generic.currency.${unit}`) : '')
    );
};

const chartTitleString = (datasetIndex: number, dataIndex: number): string => {
    const title = props.data
        ? props.data.Chart[datasetIndex].Data[dataIndex].Month.toLocaleString({ month: 'long' })
        : '';
    return upperFirst(title);
};

const chartOptions = computed<ChartOptions<'bar'>>(() => ({
    scales: {
        x: {
            stacked: false,
            grid: {
                display: false,
            },
            border: {
                display: false,
            },
            ticks: {
                color: colors.getPaletteColor('on-surface-1'),
            },
        },
        y: {
            border: {
                display: false,
                dash: (ctx) => (ctx.tick.value ? [16, 12] : undefined),
            },
            grid: {
                color: (ctx) => colors.getPaletteColor(ctx.tick.value ? 'on-surface-4' : 'on-surface-3'),
            },
            ticks: {
                padding: 10,
                color: colors.getPaletteColor('on-surface-1'),
                callback: (value) => chartLabelString(value as number, props.data.Unit),
            },
        },
    },
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: true,
        },
        tooltip: {
            mode: 'index',
            backgroundColor: 'rgba(0, 0, 0, 0.86)',
            cornerRadius: 0,
            callbacks: {
                title: (item) => chartTitleString(item[0].datasetIndex, item[0].dataIndex),
                label: (item) => chartLabelString(item.raw as number, props.data.Unit, item.dataset.label ?? ''),
            },
        },
    },
}));

const barColors = ['#FFA500', '#ffea00'];
const chartData = computed(() => {
    return {
        labels: props.data.Chart[0].Data.map((chart) => chart.Month.monthShort) || [],
        datasets: props.data.Chart.map((dataset, index) => ({
            label: dataset.Year.toString(),
            backgroundColor: barColors[index % barColors.length],
            data: dataset.Data.map((chart) => chart.Value) || [],
            // borderWidth: 0,
            // borderRadius: 6,
            // barPercentage: 0.1,
        })),
    };
});
</script>
<template>
    <q-card class="full-width full-height" square bordered>
        <q-card-section>
            <div class="row items-center justify-between" style="min-height: 36px">
                <div class="a-text-16 text-on-background-1 text-bold">
                    {{ $t(`enums.dashboard_types.${props.data.Type}`) }}
                </div>
                <div class="row q-gutter-sm items-center justify-end">
                    <q-tabs
                        v-if="
                            data.Type === DashboardType.FinishedOrdersYear || data.Type === DashboardType.InvoicesYear
                        "
                        v-model="activeTab"
                        S
                        dense
                        shrink
                        no-caps
                    >
                        <q-tab name="summary" :label="$t('dashboard.details.summary')" />
                        <q-tab name="chart" :label="$t('dashboard.details.chart')" />
                    </q-tabs>
                </div>
            </div>
        </q-card-section>
        <q-card-section>
            <div
                v-if="activeTab === 'summary'"
                class="full-height column justify-center items-center q-pr-md"
                style="min-height: 200px"
            >
                <q-markup-table flat class="full-width">
                    <thead>
                        <tr>
                            <th class="text-right text-bold"></th>
                            <th class="text-right text-bold">{{ $t('dashboard.details.count') }}</th>
                            <th class="text-right text-bold">{{ $t('dashboard.details.price') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td class="text-left">
                                <div class="a-text-16">
                                    {{
                                        data.Type === DashboardType.FinishedOrdersYear ||
                                        data.Type === DashboardType.InvoicesYear
                                            ? $t('dashboard.details.this-year')
                                            : $t('dashboard.details.this-month')
                                    }}
                                </div>
                            </td>
                            <td class="text-right">
                                <div class="a-text-28 text-bold">
                                    {{ chartLabelString(data.Summary.CurrentCount, null) }}
                                </div>
                            </td>
                            <td class="text-right">
                                <div class="a-text-28 text-bold">
                                    {{ chartLabelString(data.Summary.CurrentPrice, data.Unit) }}
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td class="text-left">
                                <div class="a-text-16">
                                    {{
                                        data.Type === DashboardType.FinishedOrdersYear ||
                                        data.Type === DashboardType.InvoicesYear
                                            ? $t('dashboard.details.previous-year')
                                            : $t('dashboard.details.previous-month')
                                    }}
                                </div>
                            </td>
                            <td class="text-right">
                                <div class="a-text-28 text-bold">
                                    {{ chartLabelString(data.Summary.PreviousCount, null) }}
                                </div>
                            </td>
                            <td class="text-right">
                                <div class="a-text-28 text-bold">
                                    {{ chartLabelString(data.Summary.PreviousPrice, data.Unit) }}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </q-markup-table>
            </div>
            <div v-if="activeTab === 'chart'" class="scroll-x full-height">
                <Bar :data="chartData" :options="chartOptions" class="full-height" style="min-height: 200px" />
            </div>
        </q-card-section>
    </q-card>
</template>
